import { useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

export type BookingStep = 'service' | 'time' | 'contacts' | 'confirm' | 'done';
const STEPS: BookingStep[] = ['service', 'time', 'contacts', 'confirm', 'done'];

interface BookingParams {
  book?: BookingStep | null;
  svc?: string | null;
  day?: string | null;
  at?: string | null;
}

/**
 * The booking sheet lives in the URL (?book=time&svc=…&day=…&at=…). Every step is a
 * history entry, so the phone's Back button goes to the previous step and finally
 * closes the sheet; closing pops all the sheet's entries at once.
 */
export function useBookingNav() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const raw = params.get('book');
  const step = STEPS.includes(raw as BookingStep) ? (raw as BookingStep) : null;
  const depth = (location.state as { bookDepth?: number } | null)?.bookDepth ?? 0;

  const go = useCallback(
    (next: BookingParams, options: { replace?: boolean } = {}) => {
      const search = new URLSearchParams(params);
      for (const [key, value] of Object.entries(next)) {
        if (value === null || value === undefined || value === '') search.delete(key);
        else search.set(key, value);
      }
      // Deferred until the tap has finished dispatching: switching the step replaces the
      // tapped element, and the drawer's outside-press check would otherwise see a
      // detached target and close the sheet.
      afterEvent(() =>
        navigate(
          { pathname: location.pathname, search: search.toString() },
          { replace: options.replace, state: { bookDepth: options.replace ? Math.max(depth, 1) : depth + 1 } },
        ),
      );
    },
    [params, navigate, location.pathname, depth],
  );

  const open = useCallback(
    (serviceId?: string) => go(serviceId ? { book: 'time', svc: serviceId, day: null, at: null } : { book: 'service' }),
    [go],
  );

  const close = useCallback(() => {
    // Swipe, backdrop and the close button may all fire for one gesture: pop once.
    if (closing) return;
    closing = true;
    window.setTimeout(() => {
      closing = false;
    }, 400);
    if (depth > 0) {
      afterEvent(() => navigate(-depth));
      return;
    }
    const search = new URLSearchParams(params);
    for (const key of ['book', 'svc', 'day', 'at']) search.delete(key);
    afterEvent(() => navigate({ pathname: location.pathname, search: search.toString() }, { replace: true, state: null }));
  }, [depth, navigate, params, location.pathname]);

  return {
    step,
    serviceId: params.get('svc'),
    day: params.get('day'),
    startsAt: params.get('at'),
    go,
    open,
    close,
    back: () => (depth > 1 ? navigate(-1) : close()),
  };
}

export const stepIndex = (step: BookingStep) => STEPS.indexOf(step);

let closing = false;

function afterEvent(fn: () => void) {
  window.setTimeout(fn, 0);
}
